// app/services/web-development/page.tsx
"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Avatar,
} from "@mui/material";
import {
  RocketLaunch,
  Schedule,
  CheckCircle,
  Code,
  DesignServices,
  Build,
  Support,
  TrendingUp,
  Speed,
  // Security,
  Public,
} from "@mui/icons-material";
import DeploymentIcon from "@mui/icons-material/RocketLaunch";
import PopoverForm from "@/components/PopoverForm";

export default function WebDevelopment() {
  const theme = useTheme();

  const whyChoosePoints = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Performance-First Development",
      description:
        "Optimized for speed, SEO, and conversions with clean, efficient code.",
    },
    {
      icon: <Code sx={{ fontSize: 40 }} />,
      title: "Custom Solutions",
      description:
        "From landing pages to enterprise platforms, tailored to your specific needs.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Cutting-Edge Tech",
      description:
        "React, Next.js, Node.js, Firebase, and cloud-native architectures.",
    },
    {
      icon: <Public sx={{ fontSize: 40 }} />,
      title: "Global Reach",
      description: "Built for SMEs, startups, and SaaS companies worldwide.",
    },
  ];

  const processSteps = [
    {
      label: "Discovery & Strategy",
      description:
        "We define goals, user journeys, and technical requirements to build the perfect foundation.",
      icon: <RocketLaunch />,
    },
    {
      label: "UI/UX Design",
      description:
        "Modern, intuitive designs aligned with your brand identity and user expectations.",
      icon: <DesignServices />,
    },
    {
      label: "Development & Integration",
      description:
        "Scalable architecture with APIs, databases, and automation integrations.",
      icon: <Build />,
    },
    {
      label: "Testing & Launch",
      description:
        "Comprehensive performance, accessibility, and cross-browser testing.",
      icon: <DeploymentIcon />,
    },
    {
      label: "Support & Growth",
      description:
        "Continuous improvements, monitoring, and feature enhancements.",
      icon: <Support />,
    },
  ];

  const techStack = {
    Frontend: ["React", "Next.js", "Vue", "TypeScript", "Material-UI"],
    Backend: ["Node.js", "Express", "Firebase", "AWS", "Python"],
    Databases: ["MongoDB", "PostgreSQL", "Firestore", "Redis"],
    DevOps: ["Docker", "GitHub Actions", "CI/CD", "AWS", "Vercel"],
  };

  const industries = [
    { name: "SaaS Platforms", icon: "💻" },
    { name: "E-commerce & Retail", icon: "🛒" },
    { name: "FinTech & Banking", icon: "💰" },
    { name: "SMEs & Agencies", icon: "🏢" },
  ];

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
          // mt: 5,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" px={5}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.2rem", md: "2.8rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Modern, Scalable Web Development for Businesses That Want to
                Grow
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: "1.15rem", md: "1.3rem" },
                }}
              >
                We design and build high-performance websites and apps that are
                fast, secure, and built to scale — tailored to your business
                goals.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <PopoverForm
                  triggerElement={
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<RocketLaunch />}
                      sx={{
                        bgcolor: "white",
                        color: theme.palette.primary.main,
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Get a Free Quote
                    </Button>
                  }
                />

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Schedule />}
                  onClick={handleConsult}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Book a Discovery Call
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: alpha("#fff", 0.1),
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🚀 Quick Start
                </Typography>
                <List>
                  {[
                    "Free initial consultation",
                    "Detailed project estimate",
                    "Flexible engagement models",
                    "Start in 1-2 weeks",
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "white" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose LinXar Labs Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Why Choose LinXar Labs for Web Development
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We combine technical expertise with business understanding to
            deliver exceptional results
          </Typography>

          <Grid container spacing={4}>
            {whyChoosePoints.map((point, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 3,
                      }}
                    >
                      {point.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {point.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {point.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Development Process Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundImage: `url('/whyLweb.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            zIndex: 0,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our Web Development Process
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A transparent, collaborative approach that ensures your
            project&apos;s success
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
            {processSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Tech Stack Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Technology Stack
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We work with modern, scalable technologies to deliver robust and
            future-proof solutions
          </Typography>

          <Grid container spacing={4}>
            {Object.entries(techStack).map(([category, technologies]) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={category}>
                <Card
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {category}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            color: "text.primary",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Industries Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Industries We Serve
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Specialized web development solutions for diverse business needs
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {industries.map((industry, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 2 }}>
                    {industry.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {industry.name}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                E-commerce Transformation
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Client: E-commerce SME in Ghana
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Challenge
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Outdated WordPress site with poor SEO, slow performance, and
                  declining conversions.
                </Typography>

                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                >
                  Solution
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Migrated to Next.js, optimized checkout flow, and integrated
                  real-time inventory management.
                </Typography>
              </Box>

              <Box
                sx={{
                  background: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ fontWeight: 700, color: theme.palette.success.main }}
                >
                  Results
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      +65%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Faster Load Times
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      +40%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Increase in Conversions
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                  Ready to See Similar Results?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  Let&apos;s discuss how we can transform your web presence and
                  drive growth.
                </Typography>
                <PopoverForm
                  triggerElement={
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: "white",
                        color: theme.palette.primary.main,
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                        },
                      }}
                    >
                      Start Your Project
                    </Button>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Ready to Build Something Amazing?
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Let&apos;s create a high-performing website that helps your business
            grow.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <PopoverForm
              triggerElement={
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RocketLaunch />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Get a Free Quote
                </Button>
              }
            />
            <Button
              variant="outlined"
              size="large"
              startIcon={<Schedule />}
              onClick={handleConsult}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Book a Discovery Call
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
